import React, {useCallback, useEffect, useMemo, useState} from "react";
import styles from "./TaskContainer.module.scss";
import TaskModel from "../../../../../../models/responses/task.model";
import {TaskIcon} from "../TaskIcon/TaskIcon";
import {Button, Dropdown, Loader, Modal, ModalHeader} from "semantic-ui-react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faAngleLeft,
    faAngleRight,
    faCirclePlay,
    faCircleStop,
    faFastForward,
    faSnooze
} from "@fortawesome/pro-light-svg-icons";
import {
    faCirclePlay as faPlaySolid,
    faCircleStop as faStopSolid,
    faFastForward as faFastForwardSolid,
    faHandshakeAngle, faCrown
} from "@fortawesome/free-solid-svg-icons";
import {useTypedSelector} from "../../../../../../store/selectors/selectors.utils";
import {TaskOptions} from "./TaskOptions";
import * as taskActions from "../../../../../../store/actions/task.actions";
import {useDispatch} from "react-redux";
import classNames from "classnames";
import {
    useActiveTaskSelector, useCompletedChecklistMapSelector,
    usePredeccessorsViewSelector,
    useSuccessorsViewSelector,
} from "../../../../../../store/selectors/task/task.selectors";
import {Maybe} from "@martin_hotell/rex-tils";
import {useRelationshipsSelector} from "../../../../../../store/selectors/relationships/relations.selectors";
import TaskStatusModel from "../../../../../../models/responses/task-status.model";
import moment from "moment";
import {
    useActiveDataVersionSelector,
    useActiveProjectSelector, useChecklistMapSelector, useCpmMapSelector, useCurrentPlaylistItemSelector,
    useProjectViewSelector, useSprintDataSelector,
    useViewWidthSelector
} from "../../../../../../store/selectors/project.selectors";
import {TaskType} from "../../../../../../models/task-type";
import {TaskEventModal} from "../TaskEventModal";
import EventTextModel from "../../../../../../models/event-text-model.enum";
import {EvaluationOptions} from "../../../../../../models/evaluation-options.enum";
import {matchPath, useNavigate} from "react-router-dom";
import RouteNameConstants from "../../../../../../constants/route-name.constants";
import {COLLECTIONS} from "../../../../../../firebase/constants";
import FirebaseUsage from "../../../../../../firebase/firebase.usage";
import {CpmLinkModel, CpmTaskModel} from "../../../../../../models/responses/cpm-task.model";
import {
    convertDateToIndex,
    convertIndexToSeconds
} from "../../../../../../utils/cpm-functions/cpm-app/functions/handleEvent";
import {SprintCategory} from "../../../../../../models/sprint.model";
import TaskProgressModal from "../TaskProgressModal";
import ProgressSlider from "./ProgressSlider";
import {TaskLinkContainer} from "../Link/TaskLinkContainer";
import {TaskPredType} from "../../../../../../models/task-pred-type.enum";
import {ChecklistItemModel} from "../../../../../../models/checklist-model";
import {useProjectMemberSelector} from "../../../../../../store/selectors/team.selectors";
import {MemberOrganizationEnum} from "../../../../../../models/member-organization.enum";
import {
    useWorkPackageMapSelector,
    useWorkPackagesSelector
} from "../../../../../../store/selectors/work-packages.selectors";
import {EventType} from "../../../../../../models/playlist-model";
import AutoPilotTaskContainer from "./AutoPilotTaskContainer";
import WorkPackageSingle from "../../../WorkPackages/components/WorkPackage";

interface InjectedProps {
  task: TaskModel;
  goToPred?: () => void;
  goToSuc?: () => void;
  hidePassBtn?: boolean;
  type?: "pred" | "succ";
  showPredType?: boolean;
  cpmTask: CpmTaskModel;
  calendarsMap: Map<string, any>;
  index?: number | null;
  navigateToTask?: (task: TaskModel) => void;
  selectNextTask?: (taskIndex: number) => void;
  predecessorToggle: boolean;
  successorToggle: boolean;
  setPredecessorToggle: React.Dispatch<React.SetStateAction<boolean>>;
  setSuccessorToggle: React.Dispatch<React.SetStateAction<boolean>>;
  sprintTasks?: Map<string, any>;
}

export const TaskContainer: React.FC<InjectedProps> = React.memo(
  ({ goToPred, goToSuc, ...props }) => {
    const [taskInfoClass, setTaskInfoClass] = useState<any>(null);
    const [spinningIcon, setSpinningIcon] = useState<boolean>(true);
    const {
        task,
        cpmTask,
        selectNextTask,
        predecessorToggle,
        successorToggle,
        setPredecessorToggle,
        setSuccessorToggle,
        sprintTasks
    } = props;
    const [sliderValue, setSliderValue] = useState<number>(0);
    const [progressChanged, setProgressChanged] = useState<boolean>(false);
    const [showEventModal, setShowEventModal] = useState<boolean>(false);
    const [showProgressModal, setShowProgressModal] = useState<boolean>(false);
    const [showWorkPackageModal, setShowWorkPackageModal] = useState<boolean>(false);
      const openPredecessors = useTypedSelector(
          (state) => state.task.predecessors.open
      );
      const openSuccessors = useTypedSelector(
          (state) => state.task.successors.open
      );
    const currentUser = useTypedSelector((state) => state.authorization.user);
    const projectMember = useProjectMemberSelector();
    const dispatch = useDispatch();
    const activeTask = useActiveTaskSelector();
    const activeProject = useActiveProjectSelector();
    const activeDataVersion = useActiveDataVersionSelector();
    const projectView = useProjectViewSelector();
    const viewWidth = useViewWidthSelector();
    const [relations, setRelations] = useState<CpmLinkModel[]>([]);
    const criticalThreshold = 5;
    const cpmMap = useCpmMapSelector();
    const workPackageMap = useWorkPackageMapSelector();
    const workPackages = useWorkPackagesSelector();
    const currentPlaylistItem = useCurrentPlaylistItemSelector();

      useEffect(() => {
          setSliderValue(task.progress && task.progress.length ?
              task.progress.sort((a, b) =>
                  b.date.seconds - a.date.seconds)[0].progress : 0);
      }, [task.progress]);

      const isTaskInSprint = (task: TaskModel, cpmTask: CpmTaskModel) => {
          if (projectView === "sprint" && sprintTasks) {
              return sprintTasks.has(task.task_code);
          } else if (task.sprint && task.sprint === activeProject!.snapshotDataVersionId) {
              return true;
          } else if (cpmTask.sprintCategory) {
              return true;
          }
          return false;
      }

    const selectSprintIcon = (taskData: TaskModel) => {
        const task = {...taskData,
            sprintCategory: projectView === "sprint" && sprintTasks ?
                sprintTasks.get(taskData.task_code) :
                cpmTask.sprintCategory ?
                    cpmTask.sprintCategory :
                    taskData.sprintCategory
        };
        const taskInSprint = isTaskInSprint(task, cpmTask);

        const activeDataVersionId = projectView === "sprint" && activeDataVersion ?
            activeDataVersion.version.dataVersionId :
            activeProject?.snapshotDataVersionId;

        const snapshotTimestamp = projectView === "sprint" && activeDataVersion ?
            activeDataVersion.version.dataVersionDate :
            activeProject?.lastSnapshotTimestamp;

        if (taskInSprint) {
            switch (task.sprintCategory) {
                case SprintCategory.START_AND_FINISH:
                    if (task.status === TaskStatusModel.COMPLETE || task.status === TaskStatusModel.DECLARED_COMPLETE) {
                        return <div className={styles.SprintIcons}>
                            <FontAwesomeIcon className={styles.PlannedStartIcon} icon={faPlaySolid}/><FontAwesomeIcon
                            className={styles.PlannedFinishIcon} icon={faStopSolid}/>
                        </div>
                    } else if (task.status === TaskStatusModel.IN_PROGRESS || task.status === TaskStatusModel.SUSPENDED) {
                        return <div className={styles.SprintIcons}>
                            <FontAwesomeIcon className={styles.PlannedStartIcon} icon={faPlaySolid}/><FontAwesomeIcon
                            icon={faCircleStop}/>
                        </div>
                    } else {
                        return <div className={styles.SprintIcons}>
                            <FontAwesomeIcon icon={faCirclePlay}/><FontAwesomeIcon icon={faCircleStop}/>
                        </div>
                    }
                case SprintCategory.START:
                    if (task.status === TaskStatusModel.NOT_STARTED || task.status === TaskStatusModel.BLOCK) {
                        return <div className={styles.SprintIcons}>
                            <FontAwesomeIcon icon={faCirclePlay}/>
                        </div>
                    } else {
                        if (task.status === TaskStatusModel.COMPLETE || task.status === TaskStatusModel.DECLARED_COMPLETE) {
                            return <div className={styles.SprintIcons}>
                                <FontAwesomeIcon className={styles.PlannedStartIcon} icon={faPlaySolid}/><FontAwesomeIcon
                                className={styles.UnplannedFinishIcon} icon={faStopSolid}/>
                            </div>
                        }
                        return <div className={styles.SprintIcons}>
                            <FontAwesomeIcon className={styles.PlannedStartIcon} icon={faPlaySolid}/>
                        </div>
                    }
                case SprintCategory.FINISH:
                    if (task.status === TaskStatusModel.COMPLETE || task.status === TaskStatusModel.DECLARED_COMPLETE) {
                        return <div className={styles.SprintIcons}>
                            <FontAwesomeIcon className={styles.PlannedFinishIcon} icon={faStopSolid}/>
                        </div>
                    } else {
                        return <div className={styles.SprintIcons}>
                            <FontAwesomeIcon icon={faCircleStop}/>
                        </div>
                    }
                case SprintCategory.PROGRESS:
                    if ((task.progress && task.progress.filter(p => p.date.seconds >= snapshotTimestamp.seconds).length)
                    || (task.status === TaskStatusModel.COMPLETE || task.status === TaskStatusModel.DECLARED_COMPLETE)) {
                        return <div className={styles.SprintIcons}>
                            <FontAwesomeIcon className={styles.FastForwardIconSolid} icon={faFastForwardSolid}/>
                        </div>
                    } else {
                        return <div className={styles.SprintIcons}>
                            <FontAwesomeIcon className={styles.FastForwardIcon} icon={faFastForward}/>
                        </div>
                    }
                default: // unplanned works
                    if (task.act_start_date && task.act_start_date.seconds > snapshotTimestamp.seconds) { // unplanned start
                        if (task.act_end_date || task.declaredCompleteTimestamp) {
                            return <div className={styles.SprintIcons}>
                                <FontAwesomeIcon className={styles.UnlannedStartIcon}
                                                 icon={faPlaySolid}/><FontAwesomeIcon
                                className={styles.UnplannedFinishIcon} icon={faStopSolid}/>
                            </div>
                        } else {
                            return <div className={styles.SprintIcons}>
                                <FontAwesomeIcon className={styles.UnplannedStartIcon} icon={faPlaySolid}/>
                            </div>
                        }
                    } else if (task.act_end_date && task.act_end_date.seconds > snapshotTimestamp.seconds) { // unplanned complete
                        return <div className={styles.SprintIcons}>
                            <FontAwesomeIcon className={styles.UnplannedFinishIcon} icon={faStopSolid}/>
                        </div>
                    } else if (task.declaredCompleteTimestamp && task.declaredCompleteTimestamp.seconds > snapshotTimestamp.seconds) {
                        return <div className={styles.SprintIcons}>
                            <FontAwesomeIcon className={styles.UnplannedFinishIcon} icon={faStopSolid}/>
                        </div>
                    }
                    if (task.status === TaskStatusModel.COMPLETE || task.status === TaskStatusModel.DECLARED_COMPLETE) {
                        if (task.startSprintId === activeDataVersionId) {
                            return <div className={styles.SprintIcons}>
                                <FontAwesomeIcon className={styles.EarlyIcon} icon={faPlaySolid}/><FontAwesomeIcon
                                className={styles.EarlyIcon} icon={faStopSolid}/>
                            </div>
                        } else {
                            return <div className={styles.SprintIcons}>
                                <FontAwesomeIcon className={styles.EarlyIcon} icon={faStopSolid}/>
                            </div>
                        }
                    } else if (task.status === TaskStatusModel.IN_PROGRESS || task.status === TaskStatusModel.SUSPENDED) {
                        return <div className={styles.SprintIcons}>
                            <FontAwesomeIcon className={styles.EarlyIcon} icon={faPlaySolid}/>
                        </div>
                    }
                    return null;
            }
            } else if (task.finishSprintId === activeDataVersionId || task.startSprintId === activeDataVersionId) {
                if (task.act_start_date && task.act_start_date.seconds > snapshotTimestamp.seconds) { // unplanned start
                    if (task.act_end_date || task.declaredCompleteTimestamp) {
                        return <div className={styles.SprintIcons}>
                            <FontAwesomeIcon className={styles.UnplannedStartIcon} icon={faPlaySolid}/><FontAwesomeIcon className={styles.UnplannedFinishIcon} icon={faStopSolid}/>
                        </div>
                    } else {
                        return <div className={styles.SprintIcons}>
                            <FontAwesomeIcon className={styles.UnplannedStartIcon} icon={faPlaySolid}/>
                        </div>
                    }
                } else if (task.act_end_date && task.act_end_date.seconds > snapshotTimestamp.seconds) { // unplanned complete
                    return <div className={styles.SprintIcons}>
                        <FontAwesomeIcon className={styles.UnplannedFinishIcon} icon={faStopSolid}/>
                    </div>
                } else if (task.declaredCompleteTimestamp && task.declaredCompleteTimestamp.seconds > snapshotTimestamp.seconds) {
                    return <div className={styles.SprintIcons}>
                        <FontAwesomeIcon className={styles.UnplannedFinishIcon} icon={faStopSolid}/>
                    </div>
                }
            if (task.status === TaskStatusModel.COMPLETE || task.status === TaskStatusModel.DECLARED_COMPLETE) {
                if (task.startSprintId === activeDataVersionId) {
                    return <div className={styles.SprintIcons}>
                        <FontAwesomeIcon className={styles.EarlyIcon} icon={faPlaySolid}/><FontAwesomeIcon
                        className={styles.EarlyIcon} icon={faStopSolid}/>
                    </div>
                } else {
                    return <div className={styles.SprintIcons}>
                        <FontAwesomeIcon className={styles.EarlyIcon} icon={faStopSolid}/>
                    </div>
                }
            } else if (task.status === TaskStatusModel.IN_PROGRESS || task.status === TaskStatusModel.SUSPENDED) {
                return <div className={styles.SprintIcons}>
                    <FontAwesomeIcon className={styles.EarlyIcon} icon={faPlaySolid}/>
                </div>
            }
                return null;
        }
      }

    // const canMakeActive = useTypedSelector(state => !state.task.predecessors.open && !state.task.successors.open);
    const loading = useTypedSelector<boolean>(
      (state) => state.task.toolbarLoadingMap[task.task_id]
    );
    const relationships = useRelationshipsSelector(task.task_id);
    const predType = useMemo(
      () =>
        relationships.length
          ? relationships[0].pred_type.replace("PR_", "")
          : "",
      [relationships]
    );
    const [delayOnMouseEnter, setDelayOnMouseEnter] = useState(null);
    const [taskInfo, setTaskInfo] = useState<string>("");
    const history = useNavigate();
    // const [underneathText, setUnderneathText] = useState<any>(null);
    const [criticalIn, setCriticalIn] = useState<any>("-");
    const [isCritical, setIsCritical] = useState<any>(false);
    const [isCriticalPath, setIsCriticalPath] = useState<any>(false);
    const [isNearCritical, setIsNearCritical] = useState<any>(false);
    const [taskOptionsOpen, setTaskOptionsOpen] = useState<boolean>(false);
    const [editReason, setEditReason] = useState<boolean>(false);
    const [modalMessage, setModalMessage] = useState<EventTextModel>(EventTextModel.COMPLETE_TASK);
      const [suspendReason, setSuspendReason] = useState<
          any
      >("No reason");
      const [evaluationResult, setEvaluationResult] = useState<
          EvaluationOptions | "No result"
      >("No result");
      const [defDate, setDefDate] = useState<string>("");

      // const [completeEmail, setCompleteEmail] = useState<any>(null);
    const predecessorsView = usePredeccessorsViewSelector();
    const successorsView = useSuccessorsViewSelector();
    const today = useMemo(() => moment(activeProject && activeProject.frozenDate ? activeProject.frozenDate.toDate() : new Date())
          .hours(0)
          .minutes(0)
          .seconds(0)
          .milliseconds(0)
          .toDate(),
        [activeProject]);

    const tomorrow = useMemo(() => moment(today)
        .clone()
        .add(1, "days")
        .hours(0)
        .minutes(0)
        .seconds(0)
        .milliseconds(0)
        .toDate(), [today]);

    const todayIndex = convertDateToIndex(today.getTime() / 1000, props.cpmTask.cal_id, props.calendarsMap);
    // const tomorrowIndex = convertDateToIndex(tomorrow.getTime() / 1000, props.cpmTask.cal_id, props.calendarsMap);
    const workingHoursPerDay: number = props.calendarsMap.get(`whd:${cpmTask.cal_id}`) || 8;

    const convertToDate = (index) => {
        return new Date(convertIndexToSeconds(index, cpmTask.cal_id, props.calendarsMap) * 1000)
    }

    function formatTimeDifference(seconds: number, workingHoursPerDay: number, deadline: boolean) {
        if (deadline && seconds < workingHoursPerDay * 3600) {
            return "Now"
        }
        // let hours: string | number, minutes: string | number, secondsLeft: string | number, secs: string | number;
        if (Math.abs(Math.round((seconds / 3600) / workingHoursPerDay)) >= 1) {
            return `${Math.floor((seconds / 3600) / workingHoursPerDay)} day${Math.floor((seconds / 3600) / workingHoursPerDay) > 1 ? "s" : ""}`;
        } else {
            return "< - - - >";
        }
    }

    function calculateDeadline(task: TaskModel) {
      let allSecs: number = 0;
      switch (task.status) {
        case TaskStatusModel.COMPLETE: {
          setCriticalIn("-")
          break;
        }
        case TaskStatusModel.DECLARED_COMPLETE: {
          setCriticalIn("-")
          break;
        }
        case TaskStatusModel.IN_PROGRESS:
        case TaskStatusModel.SUSPENDED: {
            // @ts-ignore
            allSecs = (cpmTask.lf - todayIndex) * 1800;
            setCriticalIn(formatTimeDifference(allSecs, workingHoursPerDay, true))
            break;
        }
          case TaskStatusModel.NOT_STARTED:
          case TaskStatusModel.BLOCK: {
              // @ts-ignore
              allSecs = (cpmTask.lf - todayIndex) * 1800;
              setCriticalIn(formatTimeDifference(allSecs, workingHoursPerDay, true))
              break;
          }
      }
    }

    const isActiveTask = activeTask?.task_id === task.task_id;
    const autopilotEnabled = useMemo(() =>
      projectMember ? projectMember.autopilotEnabled === undefined ? true : projectMember.autopilotEnabled
          : false, [projectMember]);

    function setTarget(task: TaskModel) {
                let actualDuration: number, originalDuration: number, hours: string | number, minutes: string | number,
                    seconds: string | number;
                switch (task.status) {
                    case TaskStatusModel.COMPLETE: {
                        originalDuration = task.targetDuration;
                        // @ts-ignore
                        actualDuration = cpmTask.ef - cpmTask.es;
                        setTaskInfo(formatTimeDifference((originalDuration - actualDuration) * 1800,
                            workingHoursPerDay,
                            false));
                        if (actualDuration > originalDuration) {
                            setTaskInfoClass("negative");
                        }
                        break;
                    }
                    case TaskStatusModel.DECLARED_COMPLETE: {
                        originalDuration = task.targetDuration;

                        // @ts-ignore
                        actualDuration = cpmTask.ef - cpmTask.es;
                        setTaskInfo(formatTimeDifference((originalDuration - actualDuration) * 1800,
                            workingHoursPerDay,
                            false));
                        if (actualDuration > originalDuration) {
                            setTaskInfoClass("negative");
                        }
                        break;
                    }
                    case TaskStatusModel.IN_PROGRESS:
                    case TaskStatusModel.SUSPENDED: {
                        const halfHours = task.targetFinishDate ?
                            convertDateToIndex(Math.floor((task.targetFinishDate.toDate().getTime() / 1000) / 1800) * 1800, cpmTask.cal_id, props.calendarsMap)
                            - convertDateToIndex(Math.floor((today.getTime() / 1000) / 1800) * 1800, cpmTask.cal_id, props.calendarsMap) :
                            task.remainingDuration;
                        setTaskInfo(formatTimeDifference(halfHours * 1800, workingHoursPerDay, false));
                        if (task.constraint_type !== "CS_ALAP") {
                            if (cpmTask.float! < workingHoursPerDay * 2) {
                                if (convertToDate(cpmTask.ls).getTime() < tomorrow.getTime()) {
                                    setIsCritical(true);
                                    setIsCriticalPath(false)
                                    setIsNearCritical(false);
                                } else {
                                    setIsCriticalPath(true)
                                    setIsNearCritical(false);
                                }
                            } else if (cpmTask.float! <= criticalThreshold * workingHoursPerDay * 2) {
                                if (convertToDate(cpmTask.ls).getTime() < tomorrow.getTime()) {
                                    setIsCritical(true);
                                    setIsCriticalPath(false)
                                    setIsNearCritical(false);
                                } else {
                                    setIsCriticalPath(false);
                                    setIsNearCritical(true);
                                }
                            }
                        } else {
                            if ((convertToDate(cpmTask.ls).getTime() / 1000) - (today.getTime() / 1000) < 86400) {
                                if (convertToDate(cpmTask.ls).getTime() < tomorrow.getTime()) {
                                    setIsCritical(true);
                                    setIsCriticalPath(false)
                                    setIsNearCritical(false);
                                } else {
                                    setIsCriticalPath(true);
                                    setIsNearCritical(false);
                                }
                            } else if ((convertToDate(cpmTask.ls).getTime() / 1000) - (today.getTime() / 1000) < 86400 * criticalThreshold) {
                                setIsCritical(false);
                                setIsNearCritical(true);
                            }
                        }
                        break;
                    }
                    case TaskStatusModel.NOT_STARTED:
                    case TaskStatusModel.BLOCK: {
                        const halfHours = task.targetFinishDate ?
                            convertDateToIndex(Math.floor((task.targetFinishDate.toDate().getTime() / 1000) / 1800) * 1800, cpmTask.cal_id, props.calendarsMap)
                            - convertDateToIndex(Math.floor((today.getTime() / 1000) / 1800) * 1800, cpmTask.cal_id, props.calendarsMap) :
                            task.remainingDuration;
                        setTaskInfo(formatTimeDifference(halfHours * 1800, workingHoursPerDay, false));
                        if (task.constraint_type !== "CS_ALAP") {
                            if (cpmTask.float! < workingHoursPerDay * 2) {
                                if (convertToDate(cpmTask.ls).getTime() <= tomorrow.getTime()) {
                                    setIsCritical(true);
                                    setIsCriticalPath(false)
                                    setIsNearCritical(false);
                                } else {
                                    setIsCriticalPath(true);
                                    setIsNearCritical(false);
                                }
                            } else if (cpmTask.float! <= criticalThreshold * workingHoursPerDay * 2) {
                                setIsCritical(false);
                                setIsNearCritical(true);
                            }
                        } else {
                            if (cpmTask.float! < workingHoursPerDay * 2) {
                                if (convertToDate(cpmTask.ls).getTime() <= tomorrow.getTime()) {
                                    setIsCritical(true);
                                    setIsCriticalPath(false)
                                    setIsNearCritical(false);
                                } else {
                                    setIsCriticalPath(true);
                                    setIsNearCritical(false);
                                }
                            } else if ((convertToDate(cpmTask.ls).getTime() / 1000) - (today.getTime() / 1000) < 86400 * criticalThreshold) {
                                setIsCritical(false);
                                setIsNearCritical(true);
                            }
                        }
                        break;
                    }
                }
            }

    useEffect(
        () => {
            let interval
            setTarget(task)
            calculateDeadline(task)
            if (task.status == TaskStatusModel.IN_PROGRESS) {
                interval = setInterval(function () {
                    const nowNearestHalfHour = Math.floor(today.getTime() / 1800000) * 1800;
                    const dayToday = today.getDay()
                    let isNonWorking = new Date(convertIndexToSeconds(convertDateToIndex(nowNearestHalfHour, cpmTask.cal_id, props.calendarsMap), cpmTask.cal_id, props.calendarsMap) * 1000).getDay() !== dayToday;
                    // let isNonWorking = convertIndexToSeconds(convertDateToIndex(nowNearestHalfHour, cpmTask.cal_id, props.calendarsMap), cpmTask.cal_id, props.calendarsMap) !== nowNearestHalfHour;
                    let timeNow = today.getTime();
                    let endDate = task.targetFinishDate ? task.targetFinishDate.toDate() : convertToDate(cpmTask.lf);
                    if (!isNonWorking) {
                        setSpinningIcon(true);
                        if (endDate.getTime() > timeNow) {
                            setTaskInfoClass(null);
                        } else if (endDate.getTime() < timeNow) {
                            setTaskInfoClass("negative");
                        }
                    } else {
                        setSpinningIcon(false);
                        if (endDate.getTime() > timeNow) {
                            setTaskInfoClass(null);
                        } else if (endDate.getTime() < timeNow) {
                            setTaskInfoClass("negative");
                        }
                    }
                    calculateDeadline(task)
                    setTarget(task)
                }, 1000);
            } else if (cpmTask.flow) {
                setSpinningIcon(false);
            }
            return () => {
                clearInterval(interval);
            };
        },
        [task]
    );

    const checkUserSeen = (task: any, currentUser: any) => {
                    const userInTask =
                        task.users &&
                        task.users.find((el: any) => el.userId === currentUser?.userId);
                    return userInTask && !userInTask.seen;
                }

    const selectActiveTask = () => {
                    if (activeTask && activeTask.task_id === task.task_id &&
                        !taskOptionsOpen &&
                        !editReason &&
                        !showEventModal &&
                        !progressChanged
                    ) {
                        dispatch(taskActions.Actions.setActiveTask(null));
                        if (predecessorToggle) setPredecessorToggle(false)
                        if (successorToggle) setSuccessorToggle(false)
                        const matchPathname = matchPath({path: '/:projectId/flow/:task_id'}, window.location.pathname);
                        const params: any = matchPathname
                            ? matchPathname.params
                            : {};
                        const activeProjectId = params.project_id || localStorage.getItem('projectId');
                        if (params.task_id) {
                            history(`/${activeProjectId}/${RouteNameConstants.FLOW}`);
                        }
                    } else {
                        if (openPredecessors && goToPred) {
                            goToPred();
                        } else if (openSuccessors && goToSuc) {
                            goToSuc();
                        } else {
                            dispatch(taskActions.Actions.setActiveTask(task));
                            console.log("task", cpmTask, {
                                ad: new Date(convertIndexToSeconds(cpmTask.ad, cpmTask.cal_id, props.calendarsMap) * 1000),
                                es: new Date(convertIndexToSeconds(cpmTask.es, cpmTask.cal_id, props.calendarsMap) * 1000),
                                ef: new Date(convertIndexToSeconds(cpmTask.ef, cpmTask.cal_id, props.calendarsMap) * 1000),
                                ls: new Date(convertIndexToSeconds(cpmTask.ls, cpmTask.cal_id, props.calendarsMap) * 1000),
                                lf: new Date(convertIndexToSeconds(cpmTask.lf, cpmTask.cal_id, props.calendarsMap) * 1000),
                                fs: new Date(convertIndexToSeconds(cpmTask.fs, cpmTask.cal_id, props.calendarsMap) * 1000),
                                ff: new Date(convertIndexToSeconds(cpmTask.ff, cpmTask.cal_id, props.calendarsMap) * 1000),
                            });
                        }
                    }
                }

    const lightSelectTask = useCallback(
        (task: Maybe<TaskModel>) => {
            clearTimeout(delayOnMouseEnter as any);
            setDelayOnMouseEnter(
                // @ts-ignore
                setTimeout(function () {
                    // @ts-ignore
                    dispatch(taskActions.Actions.lightSelectedTask(task));
                }, 500)
            );
        },
        [delayOnMouseEnter, dispatch]
    );

    const [relationsTask, setRelationsTask] = useState<any>(null);
    useEffect(() => {
        if (predecessorToggle) {
            setRelations(cpmMap.get(`${task.task_id}:preds`).map((link: string) => cpmMap.get(link)));
        } else if (successorToggle) {
            setRelations(cpmMap.get(`${task.task_id}:succs`).map((link: string) => cpmMap.get(link)));
        } else {
            setRelations([])
        }
        setRelationsTask(predecessorToggle ? 'p' : successorToggle ? 's' : null)
    }, [predecessorToggle, successorToggle]);

    const handleDelayReason = (reason: string, type: string) => {
        setModalMessage(EventTextModel.LOG_ISSUE)
        setSuspendReason(reason);
        setShowEventModal(true);
        return type === "suspend" ?
            FirebaseUsage.updateDoc(COLLECTIONS.TASKS, task.task_id, {suspendReason: reason})
                .catch((e) => console.error(e)) : null
    }

    // const taskNarrativeText = taskNarrative(task);
    const checklistMap = useChecklistMapSelector();
    const completedChecklistMap = useCompletedChecklistMapSelector();
    const overdueItemCount = useMemo(() => {
        let count = 0;
        let checklistItemCount = 0;
        let completeCount = 0;
        if (task.checklistId) {
            const checklist = checklistMap.get(task.checklistId);
            if (checklist) {
                checklistItemCount = checklist.items.length;
                checklist.items.forEach((item: ChecklistItemModel) => {
                    const isComplete = completedChecklistMap.has(`${task.task_id}:${item.checklistItemId}`);
                    const dueIndex = item.tMinusDays <= 0 ?
                        // @ts-ignore
                        cpmTask.fs ? cpmTask.fs + (item.tMinusDays * workingHoursPerDay * 2) :
                            // @ts-ignore
                            cpmTask.es + (item.tMinusDays * workingHoursPerDay * 2) :
                        // @ts-ignore
                        cpmTask.ff ? cpmTask.ff + (item.tMinusDays * workingHoursPerDay * 2) :
                            // @ts-ignore
                            cpmTask.ef + (item.tMinusDays * workingHoursPerDay * 2);

                    if (dueIndex <= todayIndex && !isComplete) {
                        count++;
                    }
                    if (isComplete) {
                        completeCount++;
                    }
                })
            }
        } else if (task.workPackage) {
            const workPackage = workPackageMap.get(task.workPackage.workPackageId!);
            if (workPackage && workPackage.checklistId) {
                const checklist = checklistMap.get(workPackage.checklistId);
                if (checklist) {
                    checklistItemCount = checklist.items.length;
                    checklist.items.forEach((item: ChecklistItemModel) => {
                        const isComplete = completedChecklistMap.has(`${workPackage.workPackageId}:${item.checklistItemId}`);
                        const dueIndex = item.tMinusDays <= 0 ?
                            // @ts-ignore
                            cpmTask.fs ? cpmTask.fs + (item.tMinusDays * workingHoursPerDay * 2) :
                                // @ts-ignore
                                cpmTask.es + (item.tMinusDays * workingHoursPerDay * 2) :
                            // @ts-ignore
                            cpmTask.ff ? cpmTask.ff + (item.tMinusDays * workingHoursPerDay * 2) :
                                // @ts-ignore
                                cpmTask.ef + (item.tMinusDays * workingHoursPerDay * 2);
                        if (dueIndex <= todayIndex && !isComplete) {
                            count++;
                        }
                        if (isComplete) {
                            completeCount++;
                        }
                    })
                }
            }
        }
        return {count, progress: (completeCount / checklistItemCount) * 100};
    }, [task, cpmTask, checklistMap, completedChecklistMap]);

    const workPackageChecklistCompleted: boolean | undefined = useMemo(() => {
        if (!task.workPackage) return false;
        const workPackage = workPackageMap.get(task.workPackage.workPackageId!);
        if (!workPackage) return false;
        const checklist = checklistMap.get(workPackage.checklistId!);
        if (!checklist) return false;
        return workPackage.completedChecklist && workPackage.completedChecklist.length >= checklist.items.length;
    }, [workPackages]);

    const getLinkEffectiveDate = (CpmTask: any, linkType: TaskPredType, lag: number) => {
        switch (linkType) {
            case TaskPredType.FS:
                return convertIndexToSeconds(CpmTask.ef + lag, CpmTask.cal_id, props.calendarsMap);
            case TaskPredType.SS:
                return convertIndexToSeconds(CpmTask.es + lag, CpmTask.cal_id, props.calendarsMap);
            case TaskPredType.SF:
                return convertIndexToSeconds(CpmTask.es + lag, CpmTask.cal_id, props.calendarsMap);
            case TaskPredType.FF:
                return convertIndexToSeconds(CpmTask.ef + lag, CpmTask.cal_id, props.calendarsMap);
            default:
                return convertIndexToSeconds(CpmTask.ef + lag, CpmTask.cal_id, props.calendarsMap);
        }
    }

    const getLinkSuccDate = (CpmTask: any, linkType: TaskPredType, lag: number) => {
        switch (linkType) {
            case TaskPredType.FS:
                return convertIndexToSeconds(CpmTask.es - lag, CpmTask.cal_id, props.calendarsMap);
            case TaskPredType.SS:
                return convertIndexToSeconds(CpmTask.es - lag, CpmTask.cal_id, props.calendarsMap);
            case TaskPredType.SF:
                return convertIndexToSeconds(CpmTask.ef - lag, CpmTask.cal_id, props.calendarsMap);
            case TaskPredType.FF:
                return convertIndexToSeconds(CpmTask.ef - lag, CpmTask.cal_id, props.calendarsMap);
            default:
                return convertIndexToSeconds(CpmTask.es - lag, CpmTask.cal_id, props.calendarsMap);
        }
    }

    const isUserTaskforce = currentUser && task.taskForce.some((el) => el === currentUser.userId || el === currentUser.userEmail)
    const isUserTaskOwner = currentUser && task.taskOwner && (task.taskOwner === currentUser.userId || task.taskOwner === currentUser.userEmail)
    const isUserAdministrator = projectMember?.grade === MemberOrganizationEnum.SUPER_ACTOR

                return (
                    <div
                        className={classNames(styles.Container, {
                            [styles.Active]: Boolean(
                                activeTask && activeTask.task_id === task.task_id
                            ),
                            [styles.MorePadding]: successorsView || predecessorsView,
                        })}
                        onClick={selectActiveTask}
                        onMouseEnter={() => lightSelectTask(task)}
                        onMouseLeave={() => lightSelectTask(null)}
                    >
                        {(task.status !== TaskStatusModel.COMPLETE &&
                                task.status !== TaskStatusModel.DECLARED_COMPLETE) &&
                            task.task_type !== TaskType.TT_FIN_MILE &&
                            task.task_type !== TaskType.TT_MILE && (
                                <p className={styles.index}>{cpmTask.index}</p>
                            )}
                        {!props.hidePassBtn && (
                            <div
                                className={classNames(styles.GoToRelationBtn, styles.GoToPred)}
                                onClick={() => {
                                    if (successorToggle) {
                                        setSuccessorToggle(false);
                                        setRelations([]);
                                    }
                                    setPredecessorToggle(!predecessorToggle);
                                }}
                                onMouseEnter={() => setTaskOptionsOpen(true)}
                                onMouseLeave={() => setTaskOptionsOpen(false)}
                            >
                                <FontAwesomeIcon icon={faAngleLeft}/>
                            </div>
                        )}
                        {checkUserSeen(task, currentUser) ? (
                            <div className={styles.Taskforcemessage}>
                                <FontAwesomeIcon icon={faHandshakeAngle}/>
                            </div>
                        ) : null}
                        {currentUser ? isUserTaskforce ? (
                            <div
                                className={`${checkUserSeen(task, currentUser) ? styles.Taskforcemessage : styles.Taskforce}`}>
                                <FontAwesomeIcon icon={faHandshakeAngle}/>
                            </div>
                        ) : isUserTaskOwner ? (
                            <div
                                className={`${checkUserSeen(task, currentUser) ? styles.Taskforcemessage : styles.Taskforce}`}>
                                <FontAwesomeIcon icon={faCrown}/>
                            </div>) : null : null}
                        {task.task_type !== TaskType.TT_TASK && task.task_type !== TaskType.TT_RSRC ? (
                            <div className={styles.IconWrap}>
                                <TaskOptions showPopupHeader={false}
                                             task={task}
                                             type={props.type}
                                             setTaskOptionsOpen={setTaskOptionsOpen}
                                             isModalOpened={showEventModal}
                                             setIsModalOpened={setShowEventModal}
                                             setEventText={setModalMessage}
                                             setSuspendReason={setSuspendReason}
                                             setEvaluationResult={setEvaluationResult}
                                             setDefDate={setDefDate}
                                             defDate={defDate}
                                             isUserTaskForce={isUserTaskforce}
                                             isUserOwner={isUserTaskOwner}
                                             isUserAdministrator={isUserAdministrator}>
                                    <>
                                        {task.status === TaskStatusModel.IN_PROGRESS &&
                                        !spinningIcon ? (
                                            <FontAwesomeIcon
                                                className={styles.SnoozeIcon}
                                                icon={faSnooze}
                                            />
                                        ) : null}
                                        {loading ? (
                                            <Loader active inline size="small"/>
                                        ) : (
                                            <TaskIcon
                                                taskListType={task.taskListType}
                                                cpmTask={cpmTask}
                                                task={task}
                                                spinningIcon={spinningIcon}
                                            />
                                        )}
                                    </>
                                </TaskOptions>
                                {/*<div className={styles.ProgressBarWrap}>*/}
                                <div className={styles.ProgressBar}>
                                    <div
                                        style={(task.status === TaskStatusModel.IN_PROGRESS || task.status === TaskStatusModel.SUSPENDED) || !task.checklistId ? {
                                            height: "100%"
                                        } : {
                                            height: `${overdueItemCount.progress}%`,
                                        }}
                                        className={task.status !== TaskStatusModel.DECLARED_COMPLETE && task.status !== TaskStatusModel.COMPLETE && task.checklist.length === 0 ? cpmTask.priority ?
                                            cpmTask.predStatus === 1 ? styles.LineGreen : cpmTask.predStatus === 2 ? styles.LineYellow : styles.LineRed : styles.LineGrey : styles.LineBlue}
                                    />
                                </div>
                                {/*</div>*/}
                            </div>
                        ) : (
                            <TaskOptions showPopupHeader={false}
                                         task={task}
                                         type={props.type}
                                         setTaskOptionsOpen={setTaskOptionsOpen}
                                         isModalOpened={showEventModal}
                                         setIsModalOpened={setShowEventModal}
                                         setEventText={setModalMessage}
                                         setSuspendReason={setSuspendReason}
                                         setEvaluationResult={setEvaluationResult}
                                         setDefDate={setDefDate}
                                         defDate={defDate}
                                         isUserTaskForce={isUserTaskforce}
                                         isUserOwner={isUserTaskOwner}
                                         isUserAdministrator={isUserAdministrator}>
                                <>
                                    <div className={styles.IconWrap}>
                                        {task.status === TaskStatusModel.IN_PROGRESS &&
                                        !spinningIcon ? (
                                            <FontAwesomeIcon
                                                className={styles.SnoozeIcon}
                                                icon={faSnooze}
                                            />
                                        ) : null}
                                        {loading ? (
                                            <Loader active inline size="small"/>
                                        ) : (
                                            <TaskIcon
                                                taskListType={task.taskListType}
                                                cpmTask={cpmTask}
                                                task={task}
                                                spinningIcon={spinningIcon}
                                            />
                                        )}
                                        <div className={styles.ProgressBarWrap}>
                                            <div className={styles.ProgressBar}>
                                                <div
                                                    style={(task.status === TaskStatusModel.IN_PROGRESS || task.status === TaskStatusModel.SUSPENDED) || !task.checklistId ? {
                                                        height: "100%"
                                                    } : {
                                                        height: `${overdueItemCount.progress}%`,
                                                    }}
                                                    className={task.status !== TaskStatusModel.DECLARED_COMPLETE && task.status !== TaskStatusModel.COMPLETE ? cpmTask.priority ?
                                                        cpmTask.predStatus === 1 ? styles.LineGreen : cpmTask.predStatus === 2 ? styles.LineYellow : styles.LineRed : styles.LineGrey : styles.LineBlue}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </>
                            </TaskOptions>
                        )}
                        <span className={styles.TaskSpan}>
              <div className={styles.TaskTopPane}>
          <div className={styles.Description}>
              <div className={styles.TaskDetails}>
                  <div className={styles.TaskName}>
                    <div className="d-flex">
                      <h4>{task.wbs}</h4>
                        {task.suspendReason ? (
                            <Dropdown scrolling icon={null}
                                      value={task.suspendReason}
                                      onChange={(e, {value}) =>
                                          handleDelayReason(value as string, "suspend")}
                                      options={activeProject!.issueCategories.map((el) => ({
                                          key: el.reason,
                                          text: el.text,
                                          value: el.reason,
                                      }))}
                                      trigger={
                                          <span
                                              onMouseEnter={() => setEditReason(true)}
                                              onMouseLeave={() => setEditReason(false)}
                                              className={styles.Reason}>{`${task.suspendReason}${
                                              task.status !== TaskStatusModel.COMPLETE && task.status !== TaskStatusModel.DECLARED_COMPLETE ?
                                                  task.reasonDate ? ` (${today.getTime() < task.reasonDate.seconds * 1000 ? '0' : Math.floor((today.getTime() - (task.reasonDate.seconds * 1000)) / 86400000)} day${Math.abs(Math.floor((today.getTime() - (task.reasonDate.seconds * 1000)) / 86400000)) === 1 ? "s" : ""})` : "" : ""}
                                            `}</span>}/>
                        ) : task.overdueReason ? (
                            <Dropdown scrolling icon={null}
                                      value={task.overdueReason}
                                      onChange={(e, {value}) =>
                                          handleDelayReason(value as string, "overdue")}
                                      options={activeProject!.issueCategories.map((el) => ({
                                          key: el.reason,
                                          text: el.text,
                                          value: el.reason,
                                      }))}
                                      trigger={
                                          <span
                                              onMouseEnter={() => setEditReason(true)}
                                              onMouseLeave={() => setEditReason(false)}
                                              className={styles.Reason}>{`${task.overdueReason}${
                                              task.status !== TaskStatusModel.COMPLETE && task.status !== TaskStatusModel.DECLARED_COMPLETE ?
                                                  task.reasonDate ? ` (${today.getTime() < task.reasonDate.seconds * 1000 ? '0' : Math.floor((today.getTime() - (task.reasonDate.seconds * 1000)) / 86400000)} day${Math.abs(Math.floor((today.getTime() - (task.reasonDate.seconds * 1000)) / 86400000)) === 1 ? "s" : ""})` : "" : ""}
                                            `}</span>}/>
                        ) : null}
                        {activeProject!.snapshotDataVersionId &&
                            selectSprintIcon(task)
                        }
                      </div>
                      <span style={{display: "flex", flexDirection: "row"}}>
                          <h5>
                              {`${task.task_name}`}
                              <span className={styles.TaskCode}>{` (${task.task_code})`}{
                                  task.progress && task.status === TaskStatusModel.IN_PROGRESS &&
                                  ` - (${sliderValue}%)`}
                              </span>
                          </h5>
                          {overdueItemCount.count > 0 &&
                              <span className={styles.OverdueItemCount}>
                                  {/*<FontAwesomeIcon icon={faTriangleExclamation}/>*/}
                                  {overdueItemCount.count} overdue checklist item{overdueItemCount.count > 1 ? "s" : ""}
                          </span>}
                          {task.workPackage &&
                              <span
                              onClick={() => setShowWorkPackageModal(true)}
                                  className={
                                  overdueItemCount && overdueItemCount.count > 0 ?
                                      styles.OverdueItemCount :
                                      workPackageChecklistCompleted ?
                                          styles.WorkPackageChecklistCompleted :
                                          styles.WorkPackageTag
                              }>
                                    | WP: {task.workPackage.workPackageName}
                          </span>}
                      </span>
                  </div>
              </div>
              {activeTask && task.task_id === activeTask.task_id && task.status === TaskStatusModel.IN_PROGRESS &&
                  (isUserTaskforce || isUserTaskOwner || isUserAdministrator || task.progress?.length) && (
                      <span className={styles.TaskProgress}
                            style={{display: "grid", gridTemplateColumns: "95% 5%", marginTop: "5px"}}
                      >
                      <ProgressSlider
                          sliderValue={sliderValue}
                          setSliderValue={setSliderValue}
                          setProgressChanged={setProgressChanged}
                          setShowProgressModal={setShowProgressModal}
                          setModalMessage={setModalMessage}
                          setShowEventModal={setShowEventModal}
                          progressChanged={progressChanged}
                          cpmTask={cpmTask}
                          activeForUser={isUserAdministrator || isUserTaskforce || isUserTaskOwner}
                      />
                    <p className={styles.SliderValue}>{sliderValue}%</p>
                </span>
                  )}
          </div>
                  {
                      showEventModal &&
                      <TaskEventModal
                          eventText={modalMessage}
                          opened={showEventModal}
                          setOpened={setShowEventModal}
                          task={task}
                          suspendReason={suspendReason}
                          navigateToTask={props.navigateToTask}
                          selectNextTask={selectNextTask}
                          setPredecessorToggle={setPredecessorToggle}
                          taskIndex={props.index !== undefined ? props.index : undefined}
                          // evaluationResult={evaluationResult}
                          defaultDate={defDate}/>
                  }
                  {showProgressModal && (
                      <TaskProgressModal
                          sliderValue={sliderValue}
                          task={task}
                          setShowModal={setShowProgressModal}
                          setProgressChanged={setProgressChanged}
                          setSliderValue={setSliderValue}
                          setShowEventModal={setShowEventModal}
                          setModalMessage={setModalMessage}
                      />
                  )}
                  {props.showPredType && predType && (
                      <div className={styles.PredTypeLabel}>{predType} - </div>
                  )}
                  <div className={styles.rightFilters}>
              {/*{taskNarrativeText && viewWidth >= 1400 && <div className={styles.TaskNarrative}>*/}
                      {/*    {taskNarrativeText}*/}
                      {/*</div>}*/}
                      {autopilotEnabled && currentPlaylistItem && currentPlaylistItem.taskId === task.task_id && !currentPlaylistItem.paused &&
                          <AutoPilotTaskContainer/>
                      }
                      {viewWidth >= 1200 &&
                          <div>
                              <div
                                  className={task.startReforecastCount ? styles.TaskInfoDatesReforecast : styles.TaskInfoDates}
                                  style={{textAlign: "center", minWidth: "100px"}}>
                                  {moment(task.act_start_date ? task.act_start_date.toDate() : new Date(convertIndexToSeconds(cpmTask.fs, cpmTask.cal_id, props.calendarsMap) * 1000))
                                      .format("DD-MMM-YY")}
                                  {task.startReforecastCount &&
                                      <span className={styles.ReforecastIcon}>
                                  {task.startReforecastCount}
                              </span>}
                              </div>
                              {projectView === 'sprint' && task.sprintFs &&
                                  <div className={styles.ForecastDate}>
                                      {moment(task.sprintFs.toDate()).format("DD-MMM-YY")}
                                  </div>}
                          </div>}
                      {viewWidth >= 1200 &&
                          <div>
                              <div
                                  className={task.endReforecastCount ? styles.TaskInfoDatesReforecast : styles.TaskInfoDates}
                                  style={{textAlign: "center", minWidth: "100px"}}>
                                  {moment(task.status === TaskStatusModel.COMPLETE || task.status === TaskStatusModel.DECLARED_COMPLETE ?
                                      task.act_end_date ? task.act_end_date.toDate() : task.declaredCompleteTimestamp!.toDate() :
                                      new Date(convertIndexToSeconds(task.status === TaskStatusModel.IN_PROGRESS || task.status === TaskStatusModel.SUSPENDED ? cpmTask.ef : cpmTask.ff, cpmTask.cal_id, props.calendarsMap) * 1000))
                                      .format("DD-MMM-YY")}
                                  {task.endReforecastCount &&
                                      <span className={styles.ReforecastIcon}>
                              {task.endReforecastCount}
                          </span>}
                              </div>
                              {projectView === 'sprint' && task.sprintFf &&
                                  <div className={styles.ForecastDate}>
                                      {moment(task.sprintFf.toDate()).format("DD-MMM-YY")}
                                  </div>}
                          </div>}
                      <div className={styles.TaskInfo} style={{textAlign: "right", minWidth: "100px"}}>
                  <div className={taskInfoClass ? styles.Negative : ""}>
                      {taskInfo}
                      <br/>
                      {taskInfoClass === "negative" && !task.overdueReason ?
                          <div className={taskInfoClass ? styles.Negative : ""}
                               style={{
                                   display: "flex",
                                   flexDirection: "column",
                                   justifyContent: "center",
                                   alignItems: "center",
                                   height: "100%",
                                   marginRight: "-20px",
                                   fontSize: "12px",
                               }}
                          >
                              <Dropdown scrolling text={"Log issue"}
                                        onChange={(e, {value}) =>
                                            handleDelayReason(value as string, "overdue")}
                                        options={activeProject!.issueCategories.map((el) => ({
                                            key: el.reason,
                                            text: el.text,
                                            value: el.reason,
                                        }))}
                              />
                          </div>
                          :
                          // <span onClick={() => setEditReason(true)} className={styles.Reason}>{task.overdueReason}</span> :
                          null
                      }
                  </div>
              </div>
              <div className={styles.criticalIn} style={{textAlign: "right"}}>
                  <p>{criticalIn}</p>
                  {isCritical ? <p className={classNames(styles.critical,
                          {
                              [styles.Blinking]: task.status === TaskStatusModel.BLOCK || task.status === TaskStatusModel.SUSPENDED
                          })}>Critical</p>
                      : isCriticalPath ? <p className={classNames(styles.criticalPath,
                          {
                              [styles.Blinking]: task.status === TaskStatusModel.BLOCK || task.status === TaskStatusModel.SUSPENDED
                          })}>Critical path</p> : null}
                  {isNearCritical && (
                      <p className={classNames(styles.nearCritical,
                          {
                              [styles.Blinking]: task.status === TaskStatusModel.BLOCK || task.status === TaskStatusModel.SUSPENDED
                          })}>Near-critical</p>
                  )}
              </div>
          </div>
              </div>
                {isActiveTask && (predecessorToggle || successorToggle) &&
                    <div className={styles.LinkSplit}></div>}
                {isActiveTask && (predecessorToggle || successorToggle) ? predecessorToggle ? relationsTask === 'p' ? relations.length === 0 ?
                            <p className={styles.LinkTitle}>No predecessors</p> :
                            <p className={styles.LinkTitle}>Predecessors</p> :
                        <p className={styles.LinkTitle}>Predecessors loading...</p> :
                    successorToggle ? relationsTask === 's' ? relations.length === 0 ?
                            <p className={styles.LinkTitle}>No successors</p> :
                            <p className={styles.LinkTitle}>Successors</p> :
                        <p className={styles.LinkTitle}>Successors loading...</p> : null : null}
                {isActiveTask && (predecessorToggle || successorToggle) ? (
                    <div className={styles.LinkViewContainer}>
                        {relations
                            .sort((a, b) => {
                                if (predecessorToggle) {
                                    const aDate = getLinkEffectiveDate(cpmMap.get(a.pred_task_id), a.pred_type, a.lag_hr_cnt);
                                    const bDate = getLinkEffectiveDate(cpmMap.get(b.pred_task_id), b.pred_type, b.lag_hr_cnt);
                                    return bDate - aDate;
                                } else {
                                    const aDate = getLinkSuccDate(cpmMap.get(a.task_id), a.pred_type, a.lag_hr_cnt);
                                    const bDate = getLinkSuccDate(cpmMap.get(b.task_id), b.pred_type, b.lag_hr_cnt);
                                    return aDate - bDate;
                                }
                            })
                            .map((el, i) => (
                                <TaskLinkContainer
                                    key={el.link_id}
                                    link={el}
                                    index={i + 1}
                                    predecessorToggle={predecessorToggle}
                                    navigateToTask={props.navigateToTask}
                                />
                            ))}
                    </div>
                ) : null}
          </span>
            {!props.hidePassBtn && goToSuc && (
                <div
                    className={classNames(styles.GoToRelationBtn, styles.GoToSuc)}
                    onClick={() => {
                        if (predecessorToggle) {
                            setPredecessorToggle(false)
                            setRelations([])
                        }
                        setSuccessorToggle(!successorToggle)
                    }}
                    onMouseEnter={() => setTaskOptionsOpen(true)}
                    onMouseLeave={() => setTaskOptionsOpen(false)}
                >
                    <FontAwesomeIcon icon={faAngleRight}/>
                </div>
            )}
            {showWorkPackageModal && <Modal
                open={true}
                closeIcon
                size={'fullscreen'}
                onClose={() => setShowWorkPackageModal(false)} className={styles.WorkPackageModal}>
                <ModalHeader
                    style={{padding: '10px 0 5px 20px', fontSize: '18px', fontWeight: 'bold'}}
                >Work Package Details
                </ModalHeader>
                <WorkPackageSingle wp={workPackageMap.get(task.workPackage!.workPackageId!)!}/>
            </Modal>}
        </div>
    );
    }
);
